import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Chart, TickCircle } from "iconsax-reactjs";
import CTAs from "./CTAs";

/* ─── simple provider badges ─── */
const StripeMark = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#635BFF" />
    <path d="M14.6 13.2c0-.7.6-1 1.5-1 1.4 0 3.1.4 4.5 1.2v-4.3c-1.5-.6-3-.9-4.5-.9-3.7 0-6.1 1.9-6.1 5.1 0 5 6.8 4.2 6.8 6.3 0 .8-.7 1.1-1.7 1.1-1.5 0-3.5-.6-5-1.5v4.4c1.7.7 3.4 1 5 1 3.8 0 6.3-1.9 6.3-5.1 0-5.4-6.8-4.4-6.8-6.3z" fill="#fff" />
  </svg>
);

const PaystackMark = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#00C3F7" />
    <path d="M9 20V12h3.2c2 0 3.3 1.1 3.3 2.9 0 1.8-1.3 2.9-3.3 2.9h-1.3V20H9zm1.9-4.7h1.1c.9 0 1.4-.4 1.4-1.1 0-.7-.5-1.1-1.4-1.1h-1.1v2.2zM17 20v-8h1.9v8H17zm3.4 0v-8h3c1.9 0 3.1 1 3.1 2.6 0 1.1-.6 1.9-1.6 2.3l1.9 3.1h-2.2l-1.6-2.8h-.7V20h-1.9zm1.9-4.4h1c.8 0 1.3-.4 1.3-1s-.5-1-1.3-1h-1v2z" fill="#fff" />
  </svg>
);

const RevolutMark = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#0075EB" />
    <path d="M10 9h6.6c2.9 0 4.9 1.6 4.9 4.2 0 2-1.1 3.3-2.8 3.9l3.3 5.9h-3.3l-2.9-5.4h-2.4V23H10V9zm3.4 6.1h3c1.4 0 2.2-.7 2.2-1.9s-.8-1.9-2.2-1.9h-3v3.8z" fill="#fff" />
  </svg>
);

// Revolut polling config
const REVOLUT_POLL_INTERVAL_MS = 3000;
const REVOLUT_POLL_TIMEOUT_MS  = 15 * 60 * 1000; // give up after 15 minutes

const PlanSelected = ({ onClose, userData }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("stripe"); // "stripe" | "paystack" | "revolut"
  const [awaitingRevolut, setAwaitingRevolut] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  const popupRef        = useRef(null);
  const pollTimerRef    = useRef(null);
  const pollStartedRef  = useRef(0);
  const subscriptionRef = useRef(null);
  const finishedRef     = useRef(false);

  // Determine plan details based on account type
  const getPlanDetails = () => {
    if (
      userData?.selectedBlock === "block1" ||
      userData?.accountType === "business"
    ) {
      return {
        name: "Business plan",
        price: "£49.99",
        period: "monthly",
        features: [
          "5 team licenses",
          "Product support",
          "Payment collection",
          "5 day free trial",
        ],
      };
    } else {
      return {
        name: "Professional Services plan",
        price: "£149",
        period: "monthly",
        features: [
          "5 team licenses",
          "Unlimited LBAs",
          "Payment collection (5% fee)",
          "Document management",
          "Priority support",
          "Advanced analytics",
          "5 day free trial",
        ],
      };
    }
  };

  const plan = getPlanDetails();

  const providerOptions = [
    {
      id: "stripe",
      label: "Pay with card (Stripe)",
      sub: "Visa, Mastercard, Amex",
      Mark: StripeMark,
    },
    {
      id: "paystack",
      label: "Pay with Paystack",
      sub: "South African cards & EFT",
      Mark: PaystackMark,
    },
    {
      id: "revolut",
      label: "Pay with Revolut",
      sub: "Revolut Pay & cards",
      Mark: RevolutMark,
    },
  ];

  // ─── Revolut: poll for activation while the checkout popup is open ────────

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const closePopup = () => {
    try {
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    } catch {
      /* ignore */
    }
    popupRef.current = null;
  };

  // Returns true when the subscription is active and we've moved on.
  const checkRevolutOnce = async () => {
    const subscriptionId = subscriptionRef.current;
    if (!subscriptionId || finishedRef.current) return false;

    try {
      const res = await fetch(
        `${API_URL}/api/subscription/verify-revolut-session?subscriptionId=${encodeURIComponent(subscriptionId)}`,
      );
      const result = await res.json();

      if (res.ok && result.success) {
        finishedRef.current = true;
        stopPolling();
        closePopup();
        // Hand over to the success page, which verifies again and logs the user in
        navigate(`/subscription/revolut-success?subscriptionId=${encodeURIComponent(subscriptionId)}`);
        return true;
      }
    } catch (err) {
      console.warn("Revolut status check failed, will retry:", err);
    }
    return false;
  };

  const startRevolutPolling = () => {
    stopPolling();
    finishedRef.current   = false;
    pollStartedRef.current = Date.now();
    setAwaitingRevolut(true);

    pollTimerRef.current = setInterval(async () => {
      const done = await checkRevolutOnce();
      if (done) return;

      const popupClosed = !popupRef.current || popupRef.current.closed;
      const timedOut    = Date.now() - pollStartedRef.current > REVOLUT_POLL_TIMEOUT_MS;

      if (popupClosed || timedOut) {
        stopPolling();
        // One last check in case payment completed just before the window closed
        const doneAfterClose = await checkRevolutOnce();
        if (!doneAfterClose) {
          setAwaitingRevolut(false);
          setLoading(false);
          setError(
            timedOut
              ? "We couldn't confirm your Revolut payment. If you were charged, sign in and check billing."
              : "Revolut checkout was closed before payment was confirmed. Select Continue to try again.",
          );
        }
      }
    }, REVOLUT_POLL_INTERVAL_MS);
  };

  // The success page (loaded inside the popup) posts this when Revolut redirects back
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "revolut-checkout-complete") {
        checkRevolutOnce();
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = async () => {
    // Open the Revolut window synchronously on the click, otherwise popup
    // blockers will block it once we're past the awaits below.
    if (selectedProvider === "revolut") {
      closePopup();
      popupRef.current = window.open("", "revolut_checkout", "width=520,height=760");
      if (popupRef.current) {
        popupRef.current.document.write(
          "<p style='font-family:sans-serif;padding:24px;color:#475569'>Opening Revolut checkout…</p>",
        );
      }
    }

    try {
      setLoading(true);
      setError("");

      if (!userData) {
        throw new Error("User data is missing");
      }

      // Determine account type
      const accountType =
        userData.selectedBlock === "block1"
          ? "business"
          : "professional_services";

      console.log("Creating company with data:", {
        companyName: userData.companyName,
        companyEmail: userData.email,
        accountType,
        ownerName: userData.name,
        ownerEmail: userData.email,
      });

      // 1. Create company and user (skip if this session already created them,
      //    e.g. the user closed the Revolut window and is retrying)
      let createResult;
      const existingPending = JSON.parse(sessionStorage.getItem("pendingRegistration") || "null");

      if (existingPending?.companyId && existingPending?.email === userData.email) {
        createResult = { companyId: existingPending.companyId, userId: existingPending.userId };
      } else {
        const createResponse = await fetch(
          `${API_URL}/api/subscription/create-company`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              companyName: userData.companyName,
              companyEmail: userData.email,
              accountType: accountType,
              ownerName: userData.name,
              ownerEmail: userData.email,
              password: userData.password,
              lawFirmName: userData.lawFirmName,
              lawFirmEmail: userData.lawFirmEmail,
              lawFirmPostcode: userData.lawFirmPostcode,
            }),
          },
        );

        createResult = await createResponse.json();

        if (!createResponse.ok) {
          throw new Error(createResult.message || "Failed to create account");
        }

        console.log("Company created:", createResult);
      }

      // 2. Create checkout session with the selected provider
      const checkoutEndpointMap = {
        stripe: `${API_URL}/api/subscription/create-checkout`,
        paystack: `${API_URL}/api/subscription/create-paystack-checkout`,
        revolut: `${API_URL}/api/subscription/create-revolut-checkout`,
      };
      const checkoutEndpoint =
        checkoutEndpointMap[selectedProvider] || checkoutEndpointMap.stripe;

      const checkoutResponse = await fetch(checkoutEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          companyId: createResult.companyId,
        }),
      });

      const checkoutResult = await checkoutResponse.json();

      if (!checkoutResponse.ok) {
        throw new Error(
          checkoutResult.message || "Failed to create checkout session",
        );
      }

      console.log("Checkout session created:", checkoutResult);

      // 3. Store user data temporarily for after payment
      const pendingData = {
        userId: createResult.userId,
        companyId: createResult.companyId,
        email: userData.email,
        name: userData.name,
        password: userData.password,
        selectedBlock: userData.selectedBlock,
        paymentProvider: selectedProvider,
        subscriptionId: checkoutResult.subscriptionId, // Revolut only
      };

      sessionStorage.setItem(
        "pendingRegistration",
        JSON.stringify(pendingData),
      );

      console.log("Stored pending registration data");

      // Stripe returns { url }, Paystack returns { checkoutUrl }, Revolut returns { checkoutUrl }
      const checkoutUrl =
        checkoutResult.url ||
        checkoutResult.checkoutUrl ||
        checkoutResult.checkout_url;

      if (!checkoutUrl) {
        throw new Error("Checkout URL missing from response");
      }

      // 4a. Revolut: load checkout in the popup and poll until active.
      //     Works even when Revolut can't redirect back (localhost / no redirect URL).
      if (selectedProvider === "revolut") {
        subscriptionRef.current = checkoutResult.subscriptionId;

        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.location.href = checkoutUrl;
          popupRef.current.focus();
          startRevolutPolling();
          return;
        }
        // Popup was blocked: fall back to same-tab redirect.
        // Revolut then redirects to /subscription/revolut-success (production URL only).
      }

      // 4b. Stripe / Paystack (or Revolut with blocked popup): full redirect
      console.log(`Redirecting to ${selectedProvider} checkout:`, checkoutUrl);
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error("Error in handleContinue:", err);
      closePopup();
      stopPolling();
      setAwaitingRevolut(false);
      setError(err.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const handleCancelRevolut = () => {
    stopPolling();
    closePopup();
    setAwaitingRevolut(false);
    setLoading(false);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');

        .dd-card {
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          font-family: 'DM Sans', sans-serif;
        }

        .dd-section-label {
          font-size: 14px; font-weight: 700;
          color: #0F172A; margin-bottom: 6px; display: block;
        }

        .dd-text {
          font-size: 13px; color: #64748B;
        }

        .dd-plan-card {
          border: 2px solid #2563EB;
          border-radius: 12px;
          padding: 20px;
          background: #FFFFFF;
        }

        .dd-feature-item {
          display: flex; align-items: center; gap: 6px;
          font-size: 13px; color: #1E293B;
        }

        .dd-provider-option {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          border: 1.5px solid #E2E8F0;
          border-radius: 10px;
          cursor: pointer;
          transition: border-color .15s, background .15s;
          background: #fff;
        }
        .dd-provider-option:hover {
          border-color: #93C5FD;
          background: #F8FAFC;
        }
        .dd-provider-option.selected {
          border-color: #2563EB;
          background: #EFF6FF;
        }
        .dd-provider-radio {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 2px solid #CBD5E1;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .dd-provider-option.selected .dd-provider-radio {
          border-color: #2563EB;
        }
        .dd-provider-radio-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #2563EB;
        }

        .dd-waiting {
          display: flex;
          align-items: center;
          gap: 10px;
          background: #EFF6FF;
          border: 1px solid #BFDBFE;
          border-radius: 8px;
          padding: 12px;
          font-size: 13px;
          color: #1E3A8A;
        }
        .dd-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #BFDBFE;
          border-top-color: #2563EB;
          border-radius: 50%;
          animation: dd-spin .8s linear infinite;
          flex-shrink: 0;
        }
        @keyframes dd-spin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) {
          .dd-spinner { animation: none; }
        }
      `}</style>

      <div
        className="dd-card"
        style={{
          width: "400px",
          display: "flex",
          flexDirection: "column",
          gap: "0",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "24px 24px 20px",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "8px",
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Chart color="#2D404E" />
            </div>
            <div>
              <div
                style={{ fontSize: "16px", fontWeight: 700, color: "#0F172A" }}
              >
                Selected plan
              </div>
              <div className="dd-text">
                Simple and flexible per-user pricing.
              </div>
            </div>
          </div>
          <button
            onClick={awaitingRevolut ? handleCancelRevolut : onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px",
            }}
          >
            <img
              src="assets/cancel.svg"
              alt="Close"
              style={{ width: "16px", height: "16px" }}
            />
          </button>
        </div>

        {/* Plan details */}
        <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "20px" }}>
          <div className="dd-plan-card">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "16px",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: 700,
                    color: "#0F172A",
                  }}
                >
                  {plan.price}
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#0F172A",
                    marginTop: "4px",
                  }}
                >
                  {plan.name}
                </div>
                <div className="dd-text">Billed {plan.period}</div>
              </div>
              <input
                type="checkbox"
                checked
                readOnly
                style={{
                  width: "18px",
                  height: "18px",
                  accentColor: "#2563EB",
                }}
              />
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {plan.features.map((feature, index) => (
                <div key={index} className="dd-feature-item">
                  <TickCircle size={18} color="#2563EB" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Payment provider selection */}
          <div>
            <span className="dd-section-label">Payment method</span>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {providerOptions.map(({ id, label, sub, Mark }) => (
                <div
                  key={id}
                  className={`dd-provider-option${selectedProvider === id ? " selected" : ""}`}
                  onClick={() => !loading && setSelectedProvider(id)}
                >
                  <div className="dd-provider-radio">
                    {selectedProvider === id && <div className="dd-provider-radio-dot" />}
                  </div>
                  <Mark size={22} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>{label}</div>
                    <div style={{ fontSize: 11, color: "#94A3B8" }}>{sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Waiting for Revolut */}
          {awaitingRevolut && (
            <div className="dd-waiting" role="status">
              <div className="dd-spinner" />
              <span>
                Complete payment in the Revolut window. This page continues automatically once
                it's confirmed.
              </span>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{ padding: "0 24px 12px" }}>
            <div
              style={{
                background: "#FEF2F2",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #FECACA",
              }}
            >
              <p style={{ fontSize: "13px", color: "#991B1B", margin: 0 }}>
                {error}
              </p>
            </div>
          </div>
        )}

        {/* Footer buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            padding: "20px 24px",
            borderTop: "1px solid #E2E8F0",
          }}
        >
          <CTAs
            type="Secondarybtn"
            text="Cancel"
            width="auto"
            onClick={awaitingRevolut ? handleCancelRevolut : onClose}
            disabled={loading && !awaitingRevolut}
          />
          <CTAs
            type="Signinbtn"
            text={
              awaitingRevolut
                ? "Waiting for payment..."
                : loading
                  ? "Processing..."
                  : "Continue"
            }
            width="auto"
            onClick={handleContinue}
            disabled={loading}
          />
        </div>
      </div>
    </>
  );
};

export default PlanSelected;